import { race } from './race.config'

const tz = race.timezone

/* US conventions for the race audience: "Saturday, July 10th, 2027" and "8:00 AM". */
const dateFmt = (opts: Intl.DateTimeFormatOptions, timeZone = tz) => new Intl.DateTimeFormat('en-US', { ...opts, timeZone })

/** 1 -> "1st", 2 -> "2nd", 10 -> "10th", 11 -> "11th", 22 -> "22nd" */
export function ordinal(n: number) {
  if (n % 100 >= 11 && n % 100 <= 13) return `${n}th`
  return `${n}${['th', 'st', 'nd', 'rd'][n % 10] ?? 'th'}`
}

/** Written-out dates use an ordinal day: "July 10th". */
const formatWritten = (d: Date, opts: Intl.DateTimeFormatOptions, timeZone = tz) =>
  dateFmt(opts, timeZone)
    .formatToParts(d)
    .map((p) => (p.type === 'day' ? ordinal(Number(p.value)) : p.value))
    .join('')

/** "Saturday, July 10th, 2027" */
export function formatLongDate(iso: string | Date) {
  const d = typeof iso === 'string' ? new Date(iso) : iso
  return formatWritten(d, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })
}

/** "July 10th, 2027" */
export function formatDate(iso: string | Date) {
  const d = typeof iso === 'string' ? new Date(iso) : iso
  return formatWritten(d, { month: 'long', day: 'numeric', year: 'numeric' })
}

/** "July 10th" */
export function formatDayMonth(iso: string | Date) {
  const d = typeof iso === 'string' ? new Date(iso) : iso
  return formatWritten(d, { month: 'long', day: 'numeric' })
}

/** Date-only strings ("2027-07-09") are calendar days, so format them without a timezone shift. */
export function formatCalendarDay(day: string, withWeekday = true) {
  const [y, m, d] = day.split('-').map(Number)
  const date = new Date(Date.UTC(y, m - 1, d, 12))
  const opts: Intl.DateTimeFormatOptions = { month: 'long', day: 'numeric', year: 'numeric' }
  if (withWeekday) opts.weekday = 'long'
  return formatWritten(date, opts, 'UTC')
}

/** "08:00" -> "8:00 AM" */
export function formatClock(hhmm: string) {
  const [h, m] = hhmm.split(':').map(Number)
  const suffix = h >= 12 ? 'PM' : 'AM'
  const h12 = h % 12 === 0 ? 12 : h % 12
  return `${h12}:${String(m).padStart(2, '0')} ${suffix}`
}

/** "16:00–19:00" -> "4:00 PM – 7:00 PM" */
export function formatHours(range: string) {
  return range
    .split('–')
    .map((t) => formatClock(t.trim()))
    .join(' – ')
}

export function formatMoney(cents: number, opts: { always2?: boolean } = {}) {
  const whole = cents % 100 === 0 && !opts.always2
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: race.currency.toUpperCase(),
    minimumFractionDigits: whole ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(cents / 100)
}

/** Calendar days from today to that date, both in the race timezone (0 on race day). */
export function daysUntil(iso: string, now = new Date()) {
  const dayNumber = (d: Date) => {
    const [y, m, day] = new Intl.DateTimeFormat('en-CA', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit' }).format(d).split('-').map(Number)
    return Date.UTC(y, m - 1, day) / 86_400_000
  }
  return Math.max(0, dayNumber(new Date(iso)) - dayNumber(now))
}

/** Race-day calendar date in the race timezone, e.g. "2027-07-10" (ISO, for calculations). */
export function raceDay() {
  return new Intl.DateTimeFormat('en-CA', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit' }).format(
    new Date(race.startsAt),
  )
}

/** Whole years between a YYYY-MM-DD birth date and race day. */
export function ageOnRaceDay(dob: string) {
  const [by, bm, bd] = dob.split('-').map(Number)
  const [ry, rm, rd] = raceDay().split('-').map(Number)
  let age = ry - by
  if (rm < bm || (rm === bm && rd < bd)) age -= 1
  return age
}

export const km = (m: number, digits = 1) => `${(m / 1000).toFixed(digits)} km`
export const miles = (m: number, digits = 1) => `${(m / 1609.344).toFixed(digits)} mi`

/** 5 km at a given pace, for the pace table. */
export function finishTime(secondsPerKm: number, distanceKm = 5) {
  const total = Math.round(secondsPerKm * distanceKm)
  const h = Math.floor(total / 3600)
  const m = Math.floor((total % 3600) / 60)
  const s = total % 60
  return h ? `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}` : `${m}:${String(s).padStart(2, '0')}`
}

/**
 * How a donor who opted in appears on the public supporters list (Donate page),
 * or null to leave them off. The raw name is whatever they typed: it may be
 * empty, one word, a couple, or include extra spaces.
 */
export function supporterLabel(name: string): string | null {
  // TODO(human): decide how donor names are shown publicly.
  void name
  return null
}
